import { styled } from '@glitz/react';
import * as React from 'react';
import { Button } from 'webapp/components/button';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { ProductPageBadge } from './ProductPageBadges';
import { Theme } from 'webapp/theme';
import { translate } from '@avensia/excite';
import { BackIcon, CloseIcon } from 'webapp/components/icons';
import { Flyout } from 'webapp/shared/flyout';
import { Position } from '@avensia/excite-ui';
import { ZIndex } from 'webapp/z-index';
import { ContentList } from '@avensia/excite-contentful';
import Image from 'webapp/components/image';
import Link from 'webapp/components/Link';

type BadgesFlyoutProps = {
  productBadges: NordicNest.Api.ProductBadgeApiModel[];
  productName?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
};

type DetailsFlyoutProps = {
  productBadge?: NordicNest.Api.ProductBadgeApiModel;
  productName?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  setOpenOnBack: (open: boolean) => void;
};

export default function ProductBadgesFlyout({ productBadges, productName, open, setOpen }: BadgesFlyoutProps) {
  const [productBadgeDetails, setProductBadgeDetails] = React.useState<NordicNest.Api.ProductBadgeApiModel | undefined>(
    undefined,
  );
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  React.useEffect(() => {
    if (productBadges.length === 1) {
      setProductBadgeDetails(productBadges[0]);
    }
  }, [productBadges]);

  const handleBadgeClick = (productBadge: NordicNest.Api.ProductBadgeApiModel | undefined) => {
    setProductBadgeDetails(productBadge);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setOpen(false);
    setDetailsOpen(false);
  };

  return (
    <>
      {productBadges.length > 1 ? (
        <>
          <Flyout visible={open} onClose={() => setOpen(false)} position={Position.Right} zIndex={ZIndex.Overlay}>
            <Header>
              <FlyoutHeading>{translate(t => t.productPage.productBadgesFlyoutHeading)}</FlyoutHeading>
              <CloseButton
                as={Theme.Transparent}
                onClick={() => setOpen(false)}
                square
                aria-label={translate(t => t.ariaLabelCloseButton)}
              >
                <CloseIcon />
              </CloseButton>
            </Header>
            <FlyoutBody>
              {productBadges.map(productBadge => (
                <ProductPageBadge
                  key={productBadge.id}
                  productBadge={productBadge}
                  onClick={() => handleBadgeClick(productBadge)}
                />
              ))}
            </FlyoutBody>
          </Flyout>
          <ProductBadgeDetailsFlyout
            productBadge={productBadgeDetails}
            productName={productName}
            open={detailsOpen}
            setOpen={handleDetailsClose}
            setOpenOnBack={setDetailsOpen}
          />
        </>
      ) : (
        <ProductBadgeDetailsFlyout
          productBadge={productBadgeDetails}
          productName={productName}
          open={open}
          setOpen={setOpen}
          setOpenOnBack={setOpen}
        />
      )}
    </>
  );
}

function ProductBadgeDetailsFlyout({ productBadge, productName, open, setOpen, setOpenOnBack }: DetailsFlyoutProps) {
  return (
    <>
      {productBadge && (
        <Flyout visible={open} onClose={() => setOpen(false)} position={Position.Right} zIndex={ZIndex.Overlay + 1}>
          <Header css={{ justifyItems: 'center' }}>
            <BackButton
              as={Theme.Transparent}
              onClick={() => setOpenOnBack(false)}
              square
              aria-label={translate(t => t.ariaLabelCloseButton)}
            >
              <BackIcon />
            </BackButton>
            <FlyoutHeading>{productBadge.text}</FlyoutHeading>
            <CloseButton
              as={Theme.Transparent}
              onClick={() => setOpen(false)}
              square
              aria-label={translate(t => t.ariaLabelCloseButton)}
            >
              <CloseIcon />
            </CloseButton>
          </Header>
          <FlyoutDetailsBody>
            <Image image={productBadge.icon} preset={'medium'} css={{ alignSelf: 'center' }} />
            <DetailsHeading>
              {productBadge.badgePublisher && <Text>{productBadge.badgePublisher}</Text>}
              <BadgeName>{productBadge.text}</BadgeName>
              <Text>{productName}</Text>
            </DetailsHeading>
            <DetailsContent>
              {productBadge.flyoutIntroText && <Text>{productBadge.flyoutIntroText}</Text>}
              {productBadge.flyoutLink && <FlyoutLink {...productBadge.flyoutLink} />}
              {productBadge.flyoutLinkText && <Text>{productBadge.flyoutLinkText}</Text>}
              <ContentList items={productBadge.pdpContent?.items} />
            </DetailsContent>
          </FlyoutDetailsBody>
        </Flyout>
      )}
    </>
  );
}

const Header = styled.div({
  height: t => t.flyout.headerHeight,
  display: 'grid',
  justifyItems: t => t.flyout.headerTextPosition,
  flexShrink: 0,
  position: 'relative',
  alignItems: 'center',
  backgroundColor: t => t.flyout.headerColor,
  gridTemplateColumns: t => `${t.sharedSpacing.l}px 1fr ${t.sharedSpacing.l}px`,
  border: { bottom: { width: 1, style: 'solid', color: t => t.borderColor } },
});

const BackButton = styled(Button, {
  position: 'absolute',
  left: t => t.spacing.xs,
});

const CloseButton = styled(Button, {
  position: 'absolute',
  right: t => t.spacing.xs,
});

const FlyoutHeading = styled.h2(
  {
    fontSize: t => t.sharedFontSize.m,
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
    maxWidth: '80%',
    gridColumn: { start: 2 },
    marginBottom: 0,
  },
  overflowEllipsisDecorator,
);

const FlyoutBody = styled.div({
  padding: { xy: t => t.sharedSpacing.m },
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: t => t.sharedSpacing.m,
});

const FlyoutDetailsBody = styled(FlyoutBody, {
  gap: t => t.sharedSpacing.xl,
});

const DetailsHeading = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const BadgeName = styled.h3({
  fontSize: t => t.fontSize.s,
  fontWeight: t => t.fontWeight?.bold,
  marginBottom: 0,
});

const Text = styled.p({
  fontSize: t => t.fontSize.xs,
  marginBottom: 0,
});

const DetailsContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: t => t.sharedSpacing.xl,
});

const FlyoutLink = styled(Link, {
  fontSize: t => t.fontSize.xs,
  textDecoration: 'underline',
});
